import React, { useState, useEffect, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, BarChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { getSportsbookReport, getSportsbookBySport } from '../api/client';
import { SiteFilter, siteParam, useSites } from '../components/SiteFilter';
import { CurrencySelector, type CurrencyMode, currencySymbol } from '../components/CurrencySelector';
import { ExportButtons } from '../components/ExportButtons';
import { formatAmount, formatNumber } from '../lib/format';
import { metricTip } from '../lib/metric-info';
import { Th } from '../components/Th';

interface DayRow { date: string; turnover: number; win: number; cancelled: number; ggr: number; bet_count: number; unique_players: number }
interface SportRow { sport: string; sport_id: number; turnover: number; ggr: number; bet_count: number; unique_players: number }

const margin = (ggr: number, turnover: number) => (turnover > 0 ? (ggr / turnover) * 100 : 0);

export const SportsbookReport: React.FC = () => {
  const { t } = useTranslation();
  const { allSites } = useSites();
  const [days, setDays] = useState<DayRow[]>([]);
  const [bySport, setBySport] = useState<SportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [siteIds, setSiteIds] = useState<number[]>([]);
  const [currencyMode, setCurrencyMode] = useState<CurrencyMode>('EUR');
  const symbol = currencySymbol(currencyMode);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const sp = siteParam(allSites, siteIds);
    const dc = currencyMode === 'BASE' ? 'EUR' : currencyMode;
    Promise.all([
      getSportsbookReport({ date_from: dateFrom, date_to: dateTo, site_ids: sp, display_currency: dc }).catch(() => ({ data: [] })),
      getSportsbookBySport({ date_from: dateFrom, date_to: dateTo, site_ids: sp, display_currency: dc }).catch(() => ({ data: [] })),
    ]).then(([d, s]) => {
      if (cancelled) return;
      setDays(d.data || []);
      setBySport(s.data || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dateFrom, dateTo, siteIds, currencyMode]);

  const totals = useMemo(() => {
    const turnover = days.reduce((a, r) => a + (r.turnover || 0), 0);
    const ggr = days.reduce((a, r) => a + (r.ggr || 0), 0);
    const win = days.reduce((a, r) => a + (r.win || 0), 0);
    const bets = days.reduce((a, r) => a + (r.bet_count || 0), 0);
    const players = days.reduce((a, r) => Math.max(a, r.unique_players || 0), 0);
    return { turnover, ggr, win, bets, players, margin: margin(ggr, turnover) };
  }, [days]);

  const sportTotal = useMemo(() => bySport.reduce((a, r) => a + (r.ggr || 0), 0), [bySport]);

  const cards = [
    { label: t('sportsbookReport.turnover'), value: `${symbol}${formatAmount(totals.turnover)}` },
    { label: t('sportsbookReport.ggr'), value: `${symbol}${formatAmount(totals.ggr)}` },
    { label: t('sportsbookReport.win'), value: `${symbol}${formatAmount(totals.win)}` },
    { label: t('sportsbookReport.margin'), value: `${formatNumber(totals.margin, 1)}%` },
    { label: t('sportsbookReport.bets'), value: formatNumber(totals.bets) },
    { label: t('sportsbookReport.bettors'), value: formatNumber(totals.players) },
  ];

  const sportChart = bySport.map((r) => ({ name: r.sport, ggr: r.ggr }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t('sportsbookReport.title')}</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-400">{t('sportsbookReport.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.from')}</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">{t('filters.to')}</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="mt-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white" />
        </div>
        <SiteFilter selected={siteIds} onChange={setSiteIds} />
        <CurrencySelector selected={currencyMode} onChange={setCurrencyMode} />
        <ExportButtons reportType="sportsbook" dateFrom={dateFrom} dateTo={dateTo} siteIds={siteParam(allSites, siteIds)} />
      </div>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-400">{c.label}</p>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Trend: turnover vs GGR */}
      <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
        <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('sportsbookReport.trendTitle')}</h2>
        {days.length === 0 ? (
          <p className="py-16 text-center text-slate-500">{loading ? t('filters.loading') : t('sportsbookReport.noData')}</p>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="date" stroke="#9ca3af" style={{ fontSize: '12px' }} />
              <YAxis yAxisId="left" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: number) => `${symbol}${formatNumber(v / 1000)}k`} />
              <YAxis yAxisId="right" orientation="right" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: number) => `${symbol}${formatNumber(v / 1000)}k`} />
              <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                formatter={(v: number) => `${symbol}${formatNumber(v)}`} />
              <Legend />
              <Bar yAxisId="left" dataKey="turnover" name={t('sportsbookReport.turnover')} fill="#94a3b8" radius={[4, 4, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="ggr" name={t('sportsbookReport.ggr')} stroke="#009EA0" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* By sport: chart + table */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-4 text-lg font-semibold text-slate-900 dark:text-white">{t('sportsbookReport.bySportChart')}</h2>
          {sportChart.length === 0 ? (
            <p className="py-12 text-center text-slate-500">{t('sportsbookReport.noData')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sportChart} layout="vertical" margin={{ left: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9ca3af" style={{ fontSize: '12px' }} tickFormatter={(v: number) => `${symbol}${formatNumber(v / 1000)}k`} />
                <YAxis type="category" dataKey="name" stroke="#9ca3af" width={80} style={{ fontSize: '12px' }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', borderRadius: '8px', color: '#fff' }}
                  formatter={(v: number) => `${symbol}${formatNumber(v)}`} />
                <Bar dataKey="ggr" name={t('sportsbookReport.ggr')} radius={[0, 4, 4, 0]}>
                  {sportChart.map((d, i) => <Cell key={d.name} fill={i === 0 ? '#009EA0' : '#94a3b8'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800">
              <tr>
                <Th tip={metricTip('sport')}>{t('sportsbookReport.sport')}</Th>
                <Th align="right" tip={metricTip('turnover')}>{t('sportsbookReport.turnover')}</Th>
                <Th align="right" tip={metricTip('ggr')}>{t('sportsbookReport.ggr')}</Th>
                <Th align="right" tip={metricTip('margin')}>{t('sportsbookReport.margin')}</Th>
                <Th align="right" tip={metricTip('betCount')}>{t('sportsbookReport.bets')}</Th>
                <Th align="right" tip={metricTip('pctOfTotal')}>{t('sportsbookReport.pctOfTotal')}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {bySport.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">{loading ? t('filters.loading') : t('sportsbookReport.noData')}</td></tr>
              ) : bySport.map((r) => (
                <tr key={r.sport_id} className="bg-white dark:bg-slate-900">
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{r.sport}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{symbol}{formatAmount(r.turnover)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{symbol}{formatAmount(r.ggr)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{formatNumber(margin(r.ggr, r.turnover), 1)}%</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{formatNumber(r.bet_count)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 dark:text-white">{formatNumber(sportTotal > 0 ? (r.ggr / sportTotal) * 100 : 0, 1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
